import { useEffect, useRef, useState } from "react";
import { point } from "@turf/helpers";
import distance from "@turf/distance";

// Freshwater "water-movement station" — the analog of useTideStage for lakes
// and streams. Finds the nearest active USGS gage by GPS and derives a
// flow/level trend plus real water temperature.
//
// Data source: USGS Water Services Instantaneous Values (IV) service — FREE,
// public, no API key. https://waterservices.usgs.gov/
//   Parameters:
//     00060  Discharge / streamflow (cfs)          → rivers/streams
//     00065  Gage height (ft)                       → rivers (stage)
//     00062  Reservoir/lake elevation (ft)          → lakes
//     00010  Water temperature (°C)                 → both
//     63680  Turbidity (FNU)                         → clarity hint
//
// Returns (mirrors useTideStage shape where practical):
//   {
//     flowStage,        // display string e.g. "Rising · 320 cfs" | "Falling · 4.2 ft"
//     trend,            // "Rising" | "Falling" | "Steady" | null  (categorical for score)
//     primaryMetric,    // "flow" | "level"
//     flowCfs, gageHeightFt, reservoirElevFt, waterTempF, turbidity,
//     siteName, siteId, sites,
//     loading, error, refresh,
//   }

const CACHE_TTL = 30 * 60 * 1000; // 30 minutes
const C_TO_F = (c) => (c * 9) / 5 + 32;

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeCache(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// Trend from the first vs. last usable reading of a series. Flow uses a
// relative threshold (2%); level uses a small absolute threshold (0.05 ft)
// because gage/elevation changes are small in magnitude but meaningful.
function trendFromSeries(series, metric) {
  const nums = series.map((p) => p.v).filter((v) => typeof v === "number" && !Number.isNaN(v));
  if (nums.length < 2) return null;
  const first = nums[0];
  const last = nums[nums.length - 1];
  const delta = last - first;
  if (metric === "flow") {
    const rel = first !== 0 ? delta / Math.abs(first) : 0;
    if (rel > 0.02) return "Rising";
    if (rel < -0.02) return "Falling";
    return "Steady";
  }
  if (delta > 0.05) return "Rising";
  if (delta < -0.05) return "Falling";
  return "Steady";
}

// USGS uses -999999 as its no-data sentinel.
function isRealValue(v) {
  return typeof v === "number" && !Number.isNaN(v) && v > -999998;
}

export default function useFlowStage({ lat, lng, enabled = true, siteId: requestedSiteId, preferLevel = false } = {}) {
  const [state, setState] = useState({
    flowStage: null,
    trend: null,
    primaryMetric: preferLevel ? "level" : "flow",
    flowCfs: null,
    gageHeightFt: null,
    reservoirElevFt: null,
    waterTempF: null,
    turbidity: null,
    siteName: null,
    siteId: null,
    sites: [],
    loading: false,
    error: null,
  });
  const [refreshToggle, setRefreshToggle] = useState(0);
  const forceRefreshRef = useRef(false);

  const refresh = () => {
    forceRefreshRef.current = true;
    setRefreshToggle((c) => c + 1);
  };

  useEffect(() => {
    if (!enabled) {
      setState((s) => ({ ...s, loading: false, error: null }));
      return undefined;
    }
    if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
      setState((s) => ({ ...s, loading: false, error: "GPS needed" }));
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);

    const run = async () => {
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      setState((s) => ({ ...s, loading: true, error: null }));

      const cacheKey = `the99:usgs:${lat.toFixed(3)},${lng.toFixed(3)}:${requestedSiteId || "auto"}:${preferLevel ? "lvl" : "flow"}`;
      const cached = readCache(cacheKey);
      if (!forceRefresh && cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
        if (!cancelled) setState({ ...cached.payload, loading: false, error: null });
        return;
      }

      // Query the IV service for all relevant params over the last 6h within an
      // expanding bounding box around the user, so we can pick the nearest gage
      // and compute a trend from the returned series.
      const fetchBox = async (halfDeg) => {
        const w = (lng - halfDeg).toFixed(6);
        const e = (lng + halfDeg).toFixed(6);
        const s = (lat - halfDeg).toFixed(6);
        const n = (lat + halfDeg).toFixed(6);
        const url =
          `https://waterservices.usgs.gov/nwis/iv/?format=json` +
          `&bBox=${w},${s},${e},${n}` +
          `&parameterCd=00060,00065,00062,00010,63680` +
          `&period=PT6H&siteStatus=active`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`USGS IV failed: ${res.status}`);
        const json = await res.json();
        return Array.isArray(json?.value?.timeSeries) ? json.value.timeSeries : [];
      };

      try {
        let series = await fetchBox(0.2);
        if (!series.length) series = await fetchBox(0.5);
        if (!series.length) series = await fetchBox(1.0);

        if (!series.length) {
          if (!cancelled) {
            setState((s) => ({
              ...s,
              loading: false,
              error: "No USGS gage found nearby",
              flowStage: null,
              trend: null,
            }));
          }
          return;
        }

        // Group timeSeries by site.
        const bySite = new Map();
        for (const ts of series) {
          const si = ts?.sourceInfo || {};
          const code = si?.siteCode?.[0]?.value;
          if (!code) continue;
          const geo = si?.geoLocation?.geogLocation || {};
          const paramCode = ts?.variable?.variableCode?.[0]?.value;
          const rawPoints = ts?.values?.[0]?.value || [];
          const points = rawPoints
            .map((p) => ({ t: p.dateTime, v: Number(p.value) }))
            .filter((p) => isRealValue(p.v));
          if (!bySite.has(code)) {
            bySite.set(code, {
              id: code,
              name: si.siteName || code,
              lat: Number(geo.latitude),
              lng: Number(geo.longitude),
              params: {},
            });
          }
          if (points.length) bySite.get(code).params[paramCode] = points;
        }

        const userPoint = point([lng, lat]);
        const sites = Array.from(bySite.values())
          .filter((st) => Number.isFinite(st.lat) && Number.isFinite(st.lng))
          .map((st) => {
            const dist = distance(userPoint, point([st.lng, st.lat]), { units: "miles" });
            const hasFlow = Array.isArray(st.params["00060"]);
            const hasStage = Array.isArray(st.params["00065"]);
            const hasElev = Array.isArray(st.params["00062"]);
            return { ...st, dist, hasFlow, hasStage, hasElev };
          })
          .sort((a, b) => a.dist - b.dist);

        // Choose the requested site if provided, else the nearest gage that
        // carries a usable movement metric for the angler's water type.
        const wantsLevel = preferLevel;
        const primaryPredicate = (st) =>
          wantsLevel ? (st.hasElev || st.hasStage) : (st.hasFlow || st.hasStage);
        const chosen =
          sites.find((st) => st.id === requestedSiteId) ||
          sites.find(primaryPredicate) ||
          sites[0] ||
          null;

        if (!chosen) {
          if (!cancelled) {
            setState((s) => ({ ...s, loading: false, error: "No usable USGS gage nearby" }));
          }
          return;
        }

        const p = chosen.params;
        const latestOf = (code) => {
          const arr = p[code];
          if (!Array.isArray(arr) || !arr.length) return null;
          return arr[arr.length - 1].v;
        };

        const flowCfs = latestOf("00060");
        const gageHeightFt = latestOf("00065");
        const reservoirElevFt = latestOf("00062");
        const waterTempC = latestOf("00010");
        const turbidity = latestOf("63680");

        // Primary metric + trend: rivers → flow (fallback stage); lakes → level.
        let primaryMetric;
        let trend;
        let stageValueLabel;
        if (wantsLevel) {
          primaryMetric = "level";
          if (Array.isArray(p["00062"])) {
            trend = trendFromSeries(p["00062"], "level");
            stageValueLabel = reservoirElevFt != null ? `${reservoirElevFt.toFixed(1)} ft` : null;
          } else if (Array.isArray(p["00065"])) {
            trend = trendFromSeries(p["00065"], "level");
            stageValueLabel = gageHeightFt != null ? `${gageHeightFt.toFixed(1)} ft` : null;
          }
        } else {
          primaryMetric = "flow";
          if (Array.isArray(p["00060"])) {
            trend = trendFromSeries(p["00060"], "flow");
            stageValueLabel = flowCfs != null ? `${Math.round(flowCfs).toLocaleString()} cfs` : null;
          } else if (Array.isArray(p["00065"])) {
            primaryMetric = "level";
            trend = trendFromSeries(p["00065"], "level");
            stageValueLabel = gageHeightFt != null ? `${gageHeightFt.toFixed(1)} ft` : null;
          }
        }

        const flowStage = trend
          ? `${trend}${stageValueLabel ? ` · ${stageValueLabel}` : ""}`
          : stageValueLabel || null;

        const payload = {
          flowStage,
          trend: trend || null,
          primaryMetric,
          flowCfs: flowCfs != null ? Math.round(flowCfs) : null,
          gageHeightFt: gageHeightFt != null ? Number(gageHeightFt.toFixed(2)) : null,
          reservoirElevFt: reservoirElevFt != null ? Number(reservoirElevFt.toFixed(2)) : null,
          waterTempF: waterTempC != null ? Math.round(C_TO_F(waterTempC)) : null,
          turbidity: turbidity != null ? Number(turbidity.toFixed(1)) : null,
          siteName: chosen.name,
          siteId: chosen.id,
          sites: sites.map((st) => ({
            id: st.id,
            name: st.name,
            lat: st.lat,
            lng: st.lng,
            dist: Number(st.dist.toFixed(1)),
            hasFlow: st.hasFlow,
            hasStage: st.hasStage,
            hasElev: st.hasElev,
          })),
        };

        writeCache(cacheKey, { fetchedAt: Date.now(), payload });
        if (!cancelled) setState({ ...payload, loading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        const msg = err?.name === "AbortError" ? "USGS request timed out" : (err?.message || "USGS fetch failed");
        setState((s) => ({ ...s, loading: false, error: msg }));
      }
    };

    run();
    const interval = setInterval(run, 30 * 60 * 1000);
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeoutId);
      clearInterval(interval);
    };
  }, [lat, lng, enabled, refreshToggle, requestedSiteId, preferLevel]);

  return { ...state, refresh };
}
